import layoutStyles from '../ProductPageLayout/ProductPageLayout.module.css'
import { FC, useState, useRef, useMemo, useEffect } from 'react'
import { CDN_BASE as CDN_ROOT } from '~/lib/cdn'
import { useLocalizedRouter } from '~/components/LocalizedLink/LocalizedLink'
import { FormattedMessage, useIntl } from 'react-intl'
import { Slider } from '~/components/Slider/Slider'
import {
  ButtonOptionsType,
  ButtonSelect,
} from '~/components/ButtonSelect/ButtonSelect'
import { CustomButton } from '~/components/CustomButton/CustomButton'
import { Modal } from '~/components/Modal/Modal'
import { ShareConfigModal } from '~/components/ShareConfigModal/ShareConfigModal'
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart'
import LocalPhoneIcon from '@mui/icons-material/LocalPhone'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import {
  WorkTableViewer,
  WorkTableViewerRef,
} from '~/components/WorkTableViewer/WorkTableViewer'
import SelectColor from '~/components/SelectColor/SelectColor'
import { getColorItemByName } from '~/utils/colorDictionary'
import { useCart } from '~/context/cartContext'
import {
  WORK_TABLE_WIDTH_RANGE,
  WORK_TABLE_WIDTH_STEP,
  WORK_TABLE_HEIGHT_OPTIONS,
  WORK_TABLE_DEFAULTS,
  WORK_TABLE_DEPTH,
  WorkTableHeight,
  WorkTableDimensionsComponent,
  WorkTableTextureComponent,
  WorkTableDrawersComponent,
  ProductComponent,
  getWorkTableDrawerCount,
} from '~/components/ProductPage/productTypes/workTable'
import { useMediaQuery, IconButton, Tooltip } from '@mui/material'
import { ConfiguratorPageShell } from '~/components/ConfiguratorPageShell/ConfiguratorPageShell'
import { RequestOfferModal } from '~/components/RequestOfferModal/RequestOfferModal'
import { useFurniture } from '~/context/furnitureContext'
import { findChipboardIdByColorName } from '~/utils/furnitureBySku'
import { WorkTableQuoteProvider } from '~/context/workTableQuoteContext'
import { useQuote, useQuoteResult } from '~/context/pricingQuoteContext'
import type { PricingQuoteResult } from '~/utils/pricingQuote'
import { useServiceOptions } from '~/context/serviceOptionsContext'

export type { ProductComponent } from '~/components/ProductPage/productTypes/workTable'

export type PredefinedValue = {
  dimensions?: { width: number; height: number; depth: number }
  price?: number
  screenshot?: string
  quote?: PricingQuoteResult
}

interface WorkTableProductPageProps {
  components: () => ProductComponent[]
  name: string
}

const CDN_BASE = `${CDN_ROOT}/work-table/renders`

const DEFAULT_RENDER_SLUG = 'natural-accacia'

const heightOptions: ButtonOptionsType<string>[] =
  WORK_TABLE_HEIGHT_OPTIONS.map((h) => ({
    value: String(h),
    label: (
      <span>
        {h} <FormattedMessage id="homepage.configurator.dimensions.cm" />
      </span>
    ),
  }))

type DrawersValue = 'yes' | 'no'

const drawersOptions: ButtonOptionsType<DrawersValue>[] = [
  {
    value: 'no',
    label: (
      <FormattedMessage id="homepage.configurator.work-table.drawers.no" />
    ),
  },
  {
    value: 'yes',
    label: (
      <FormattedMessage id="homepage.configurator.work-table.drawers.yes" />
    ),
  },
]

type WorkTableTab = 'characteristics' | 'colors'

const tabOptions: ButtonOptionsType<WorkTableTab>[] = [
  {
    value: 'characteristics',
    label: <FormattedMessage id="cart.tableHeader.characteristics" />,
  },
  {
    value: 'colors',
    label: <FormattedMessage id="homepage.configurator.colors.title" />,
  },
]

export const ProductPage: FC<WorkTableProductPageProps> = ({
  components,
  name,
}) => {
  const currentComponents = components()

  const dimensionsComponent = currentComponents.find(
    (c) => c.type === 'dimensions'
  ) as WorkTableDimensionsComponent | undefined

  const textureComponent = currentComponents.find(
    (c) => c.type === 'texture'
  ) as WorkTableTextureComponent | undefined

  const drawersComponent = currentComponents.find(
    (c) => c.type === 'drawers'
  ) as WorkTableDrawersComponent | undefined

  const width = dimensionsComponent?.width ?? WORK_TABLE_DEFAULTS.width
  const height = dimensionsComponent?.height ?? WORK_TABLE_DEFAULTS.height
  const depth = dimensionsComponent?.depth ?? WORK_TABLE_DEPTH
  const selectedTexture = textureComponent?.selectedTexture
  const hasDrawers =
    drawersComponent?.hasDrawers ?? WORK_TABLE_DEFAULTS.hasDrawers

  const furniture = useFurniture()
  const chipboardId = useMemo(
    () => findChipboardIdByColorName(furniture, selectedTexture),
    [furniture, selectedTexture]
  )

  return (
    <WorkTableQuoteProvider
      sku="work-table"
      chipboardId={chipboardId}
      width={width}
      height={height}
      depth={depth}
      hasDrawers={hasDrawers}
      drawerCount={getWorkTableDrawerCount(width)}
    >
      <WorkTableProductPageContent
        name={name}
        currentComponents={currentComponents}
        dimensionsComponent={dimensionsComponent}
        textureComponent={textureComponent}
        drawersComponent={drawersComponent}
        width={width}
        height={height}
        depth={depth}
        selectedTexture={selectedTexture}
        hasDrawers={hasDrawers}
      />
    </WorkTableQuoteProvider>
  )
}

const round10 = (n: number): number => Math.round(n / 10) * 10

interface WorkTablePriceSectionProps {
  onAddToCart: (price: number) => void
  onRequestOffer: () => void
  /** Lift the computed price so the parent can use it (cart, offer summary). */
  onPriceChange: (price: number | null) => void
}

/**
 * Price bar for the work-table page. Rendered as the shell's `priceSection`, so
 * it lives inside the ServiceOptionsProvider and can read the delivery/assembly
 * toggles. Delivery & assembly are opt-in: the backend total already includes
 * them, so they are subtracted by default and added back when ticked — matching
 * <ProductPrice> and the catalog "de la" price (useCatalogPrice).
 */
const WorkTablePriceSection: FC<WorkTablePriceSectionProps> = ({
  onAddToCart,
  onRequestOffer,
  onPriceChange,
}) => {
  const intl = useIntl()
  const quote = useQuote()
  const { delivery, assembly } = useServiceOptions()

  const amountOf = (groupSku: string): number =>
    quote?.breakdown.find((b) => b.groupSku === groupSku)?.amount ?? 0
  const deliveryAmount = amountOf('delivery')
  const assemblyAmount = amountOf('assembly')

  const price = quote
    ? Math.round(
        quote.total -
          deliveryAmount -
          assemblyAmount +
          (delivery ? deliveryAmount : 0) +
          (assembly ? assemblyAmount : 0)
      )
    : null
  const originalPrice = price !== null ? round10(price / 0.7) : null

  useEffect(() => {
    onPriceChange(price)
  }, [price, onPriceChange])

  const breakdownTooltip = quote ? (
    <div className={layoutStyles.priceBreakdown}>
      {quote.breakdown
        .filter((item) => Math.round(item.amount) > 0)
        // delivery/assembly are shown only when selected (opt-in services)
        .filter((item) =>
          item.groupSku === 'delivery'
            ? delivery
            : item.groupSku === 'assembly'
              ? assembly
              : true
        )
        .map((item) => (
          <div key={item.groupId} className={layoutStyles.priceBreakdownRow}>
            <span>
              {intl.formatMessage({
                id: `pricing.breakdown.${item.groupSku}`,
                defaultMessage: item.groupName,
              })}
            </span>
            <span>
              {Math.round(item.amount)}&nbsp;
              <FormattedMessage id="homepage.configurator.price.currencyLei" />
            </span>
          </div>
        ))}
      <div className={layoutStyles.priceBreakdownTotal}>
        <span>
          <FormattedMessage id="pricing.breakdown.total" />
        </span>
        <span>
          {price}&nbsp;
          <FormattedMessage id="homepage.configurator.price.currencyLei" />
        </span>
      </div>
    </div>
  ) : null

  return (
    <div className={layoutStyles.priceCont}>
      <div className={layoutStyles.priceRow}>
        <div className={layoutStyles.priceBox}>
          <p className={layoutStyles.newPrice}>
            {price !== null ? (
              <>
                {price}&nbsp;
                <FormattedMessage id="homepage.configurator.price.currencyLei" />
              </>
            ) : (
              '...'
            )}
          </p>
          {breakdownTooltip && (
            <Tooltip
              title={breakdownTooltip}
              arrow
              enterTouchDelay={0}
              leaveTouchDelay={6000}
            >
              <HelpOutlineIcon
                color="action"
                sx={{ fontSize: 20, ml: 0.5, cursor: 'help' }}
                aria-label={intl.formatMessage({
                  id: 'pricing.breakdown.title',
                  defaultMessage: 'Detalierea prețului',
                })}
              />
            </Tooltip>
          )}
          {originalPrice !== null && (
            <p
              className={layoutStyles.crossedPrice}
              style={{ margin: '7px 0 0 10px', fontSize: 13 }}
            >
              {originalPrice}&nbsp;
              <FormattedMessage id="homepage.configurator.price.currencyLei" />
            </p>
          )}
        </div>

        <div className={layoutStyles.addToCartButtonContainer}>
          <div className={layoutStyles.cartButtonWrapper}>
            <CustomButton
              icon={<LocalPhoneIcon />}
              size="medium"
              variant="primary"
              onClick={onRequestOffer}
            >
              <FormattedMessage id="homepage.button.requestOffer" />
            </CustomButton>
          </div>
          <div className={layoutStyles.cartButtonWrapper}>
            <IconButton
              onClick={() => price !== null && onAddToCart(price)}
              className={layoutStyles.cartIconButton}
            >
              <ShoppingCartIcon />
            </IconButton>
          </div>
        </div>
      </div>
    </div>
  )
}

interface ContentProps {
  name: string
  currentComponents: ProductComponent[]
  dimensionsComponent: WorkTableDimensionsComponent | undefined
  textureComponent: WorkTableTextureComponent | undefined
  drawersComponent: WorkTableDrawersComponent | undefined
  width: number
  height: number
  depth: number
  selectedTexture: string | undefined
  hasDrawers: boolean
}

const WorkTableProductPageContent: FC<ContentProps> = ({
  name,
  currentComponents,
  dimensionsComponent,
  textureComponent,
  drawersComponent,
  width,
  height,
  depth,
  selectedTexture,
  hasDrawers,
}) => {
  const router = useLocalizedRouter()
  const { addItem } = useCart()
  const quoteResult = useQuoteResult()
  const isMobile = useMediaQuery('(max-width: 768px)')
  const [activeTab, setActiveTab] = useState<WorkTableTab>('characteristics')
  const [isCartModalOpen, setIsCartModalOpen] = useState(false)
  const [isShareModalOpen, setIsShareModalOpen] = useState(false)
  const [isOfferModalOpen, setIsOfferModalOpen] = useState(false)
  const [shareUrl, setShareUrl] = useState('')
  const [offerConfigUrl, setOfferConfigUrl] = useState('')
  // Price is computed in <WorkTablePriceSection> (inside the service-options
  // provider) and lifted up here for the cart + offer summary.
  const [displayPrice, setDisplayPrice] = useState<number | null>(null)
  const viewerRef = useRef<WorkTableViewerRef>(null)

  const renderSlug = useMemo(() => {
    if (!selectedTexture) return DEFAULT_RENDER_SLUG
    return getColorItemByName(selectedTexture)?.fileURL ?? DEFAULT_RENDER_SLUG
  }, [selectedTexture])

  const renderImages = useMemo(
    () => [1, 2, 3, 4].map((i) => `${CDN_BASE}/large/${i}/${renderSlug}.jpg`),
    [renderSlug]
  )

  const renderThumbnails = useMemo(
    () => [1, 2, 3].map((i) => `${CDN_BASE}/thumbnails/${i}/${renderSlug}.jpg`),
    [renderSlug]
  )

  const buildShareUrl = () => {
    if (typeof window === 'undefined') return ''
    const pathSegments = window.location.pathname.split('/')
    const locale = pathSegments[1] || 'ro'
    const params = new URLSearchParams({
      width: String(width),
      height: String(height),
      drawers: String(hasDrawers),
      ...(selectedTexture ? { texture: selectedTexture } : {}),
    })
    return `${window.location.origin}/${locale}/configurator/work-table?${params}`
  }

  const handleAddToCart = (price: number) => {
    const screenshot = viewerRef.current?.captureScreenshot() ?? undefined
    const cartComponents = currentComponents.map((c) =>
      c.type === 'price' ? { ...c, price } : c
    )
    addItem('work-table', cartComponents, {
      screenshot,
      quote: quoteResult ?? undefined,
    })
    setIsCartModalOpen(true)
  }

  const renderDimensionsSection = () => (
    <div className={layoutStyles.dimensionsGrid}>
      <label className={layoutStyles.dimensionLabel}>
        <p className={layoutStyles.dimensionTitle}>
          <FormattedMessage id="homepage.configurator.dimensions.width" />
        </p>
        <div className={layoutStyles.dimensionControl}>
          <Slider
            min={WORK_TABLE_WIDTH_RANGE[0]}
            max={WORK_TABLE_WIDTH_RANGE[1]}
            step={WORK_TABLE_WIDTH_STEP}
            value={width}
            onChange={(val) => dimensionsComponent?.setWidth(val)}
          />
        </div>
      </label>

      <label className={layoutStyles.dimensionLabel}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <p className={layoutStyles.dimensionTitle}>
            <FormattedMessage id="homepage.configurator.dimensions.height" />
            <span
              className={layoutStyles.dimensionsTooltipContainer}
              style={{ marginTop: 0 }}
              title="Alege înălțimea mesei de lucru"
            >
              <HelpOutlineIcon color="action" sx={{ fontSize: 18 }} />
            </span>
          </p>
        </div>
        <ButtonSelect
          options={heightOptions}
          defaultSelected={String(height)}
          onChange={(val) =>
            dimensionsComponent?.setHeight(Number(val) as WorkTableHeight)
          }
        />
      </label>

      {drawersComponent && (
        <label
          className={layoutStyles.dimensionLabel}
          style={{ marginTop: 10 }}
        >
          <p className={layoutStyles.dimensionTitle}>
            <FormattedMessage id="homepage.configurator.work-table.drawers.title" />
          </p>
          <ButtonSelect<DrawersValue>
            options={drawersOptions}
            defaultSelected={drawersComponent.hasDrawers ? 'yes' : 'no'}
            onChange={(val) => drawersComponent.setHasDrawers(val === 'yes')}
          />
        </label>
      )}
    </div>
  )

  const renderColorsSection = () => (
    <>
      {textureComponent && (
        <label className={layoutStyles.colorsLabel}>
          <p className={layoutStyles.dimensionTitle}>
            <FormattedMessage id="homepage.configurator.work-table.texture.title" />
          </p>
          <SelectColor
            colors={textureComponent.textures}
            onChange={(value) => textureComponent.setSelectedTexture(value)}
            defaultSelected={textureComponent.selectedTexture}
            size="medium"
          />
        </label>
      )}
    </>
  )

  // h2, not h1: the prerender-safe canonical h1 for this route is rendered by
  // <SeoContentSection> (main.tsx), outside the lazy route boundary.
  const heading = (
    <h2 className={layoutStyles.title}>
      <FormattedMessage id={name} />
    </h2>
  )

  const priceSection = (
    <WorkTablePriceSection
      onAddToCart={handleAddToCart}
      onRequestOffer={() => {
        setOfferConfigUrl(buildShareUrl())
        setIsOfferModalOpen(true)
      }}
      onPriceChange={setDisplayPrice}
    />
  )

  const controls = isMobile ? (
    <>
      <div className={layoutStyles.mobileNavChips}>
        <ButtonSelect<WorkTableTab>
          options={tabOptions}
          defaultSelected={activeTab}
          size="medium"
          className={layoutStyles.mobileNavChipsRow}
          onChange={(val) => setActiveTab(val)}
        />
      </div>

      <div
        className={layoutStyles.mobileCorePanel}
        style={{ flexDirection: 'column', alignItems: 'start' }}
      >
        {activeTab === 'characteristics' && renderDimensionsSection()}
        {activeTab === 'colors' && renderColorsSection()}
      </div>
    </>
  ) : (
    <>
      <h3 className={layoutStyles.dimensionsHeaderTitle}>
        <FormattedMessage id="homepage.configurator.dimensions.title" />
      </h3>
      {renderDimensionsSection()}

      <h3
        className={layoutStyles.dimensionsHeaderTitle}
        style={{ marginTop: 20 }}
      >
        <FormattedMessage id="homepage.configurator.colors.title" />
      </h3>
      {renderColorsSection()}
    </>
  )

  const modals = (
    <>
      <Modal isOpen={isCartModalOpen} onClose={() => setIsCartModalOpen(false)}>
        <h3>
          <FormattedMessage id="modal.addedToCart.title" />
        </h3>
        <div className={layoutStyles.buttonRow}>
          <CustomButton onClick={() => router.push('/cart')}>
            <FormattedMessage id="homepage.button.openCart" />
          </CustomButton>
          <CustomButton variant="grey" onClick={() => router.push('/')}>
            <FormattedMessage id="homepage.button.backToPurchase" />
          </CustomButton>
        </div>
      </Modal>

      <ShareConfigModal
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        url={shareUrl}
      />
      <RequestOfferModal
        isOpen={isOfferModalOpen}
        onClose={() => setIsOfferModalOpen(false)}
        configUrl={offerConfigUrl}
        productSummary={`Masă de lucru ${width}×${depth}×${height}cm${hasDrawers ? ' cu sertare' : ''} — ${displayPrice ?? '...'} lei`}
      />
    </>
  )

  return (
    <ConfiguratorPageShell
      viewer={
        <WorkTableViewer
          ref={viewerRef}
          width={width}
          depth={depth}
          height={height}
          selectedColor={selectedTexture}
          hasDrawers={hasDrawers}
          drawerCount={getWorkTableDrawerCount(width)}
          onShare={() => {
            setShareUrl(buildShareUrl())
            setIsShareModalOpen(true)
          }}
          onExport={() => viewerRef.current?.exportModel()}
        />
      }
      renderThumbnails={renderThumbnails}
      heading={heading}
      priceSection={priceSection}
      controls={controls}
      renderImages={renderImages}
      showOrderProgress
      orderProgressVariant="table"
      showInfoBar
      modals={modals}
    />
  )
}
